import { useState } from "react";
import { Link } from "react-router-dom";

import MeridianLockup from "../components/MeridianLockup";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase";

/**
 * MER-168: forgot-password reset flow. Triggers Supabase's
 * `resetPasswordForEmail` which emails the user a magic link back to
 * `/set-password` (MER-519) with a recovery token, where they choose a new
 * password. Previously this pointed at `/login`, which exchanged the token
 * into a transient session but never showed a set-password form, so the
 * user was logged in once and still had no reusable password.
 *
 * No verification beyond email format here: Supabase responds the same
 * way regardless of whether the address has an account, by design (so
 * a bad actor can't enumerate users). UI just confirms the email was
 * sent if the address looks valid.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      if (!isSupabaseConfigured()) {
        throw new Error("Password reset is not available in this build.");
      }
      const supabase = getSupabase();
      const redirectTo = `${window.location.origin}/set-password`;
      const { error: supabaseError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo,
      });
      if (supabaseError) throw supabaseError;
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Reset failed");
    }
  }

  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 380,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 16, marginBottom: 4 }}>
          <MeridianLockup size="lg" align="stack" />
          <h1 style={{ fontSize: 18, fontWeight: 600, color: "var(--text)" }}>
            Reset your password
          </h1>
        </div>

        {status === "sent" ? (
          <div
            role="status"
            style={{
              fontSize: 13,
              color: "var(--brand-deep)",
              background: "rgba(38, 168, 159, 0.08)",
              padding: "12px 14px",
              borderRadius: 8,
              textAlign: "center",
            }}
          >
            If that email is registered, we&apos;ve sent a reset link to it. Check your inbox.
          </div>
        ) : (
          <>
            <label style={{ display: "grid", gap: 6, fontSize: 13 }}>
              <span style={{ color: "var(--text-muted)" }}>Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                data-testid="forgot-email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                style={inputStyle}
              />
            </label>

            {error ? (
              <div
                role="alert"
                style={{
                  fontSize: 13,
                  color: "var(--flag-red)",
                  background: "rgba(192, 57, 43, 0.08)",
                  padding: "10px 12px",
                  borderRadius: 8,
                }}
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="forgot-submit"
              style={primaryButtonStyle}
            >
              {status === "sending" ? "Sending..." : "Send reset link"}
            </button>
          </>
        )}

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 8,
            paddingTop: 12,
            textAlign: "center",
          }}
        >
          <Link
            to="/login"
            style={{
              fontSize: 12,
              color: "var(--brand-deep)",
              textDecoration: "none",
            }}
          >
            Back to sign in
          </Link>
        </div>
      </form>
    </main>
  );
}

const inputStyle: React.CSSProperties = {
  font: "inherit",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-elevated)",
  color: "var(--text-primary)",
};

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
};
